import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { listTdsSections, createTdsSection, alterTdsSection, deleteTdsSection, type TdsSection } from '@/api/accountsBooks';

interface Draft { code: string | null; newCode: string; nature: string; oldSection: string; rateCompany: string; rateOther: string; singleLimit: string; yearLimit: string; kind: TdsSection['kind'] }

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';
const money = (v: number | null) => (v == null ? '—' : `₹${formatINR(v)}`);
const num = (v: string) => (v.trim() === '' ? null : Number(v));

/** Thresholds in words, as the prototype shows them. */
function limits(r: TdsSection) {
  if (r.kind === 'rent') return `Above ${money(r.monthLimit)} a month`;
  if (r.kind === 'goods') return `Above ${money(r.yearLimit)} a year (on the excess)`;
  const parts = [r.singleLimit != null && `${money(r.singleLimit)} per bill`, r.yearLimit != null && `${money(r.yearLimit)} a year`].filter(Boolean);
  return parts.length ? parts.join(' or ') : 'Every bill';
}

export function TdsSectionsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [draft, setDraft] = useState<Draft | null>(null);

  const query = useQuery({ queryKey: ['acc-books', 'tds-sections'], queryFn: listTdsSections });
  const rows = query.data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books', 'tds-sections'] });

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        nature: d.nature.trim(), oldSection: d.oldSection.trim() || null,
        rateCompany: Number(d.rateCompany || 0), rateOther: Number(d.rateOther || 0),
        singleLimit: num(d.singleLimit), yearLimit: num(d.yearLimit),
      };
      return d.code ? alterTdsSection(d.code, body) : createTdsSection({ ...body, code: d.newCode.trim() });
    },
    onSuccess: () => { toast.push('TDS section saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const delMut = useMutation({
    mutationFn: (code: string) => deleteTdsSection(code),
    onSuccess: () => { toast.push('TDS section deleted', 'success'); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const open = (r?: TdsSection) =>
    setDraft(r
      ? { code: r.code, newCode: r.code, nature: r.nature, oldSection: r.oldSection ?? '', rateCompany: String(r.rateCompany), rateOther: String(r.rateOther), singleLimit: r.singleLimit == null ? '' : String(r.singleLimit), yearLimit: r.yearLimit == null ? '' : String(r.yearLimit), kind: r.kind }
      : { code: null, newCode: '', nature: '', oldSection: '', rateCompany: '', rateOther: '', singleLimit: '', yearLimit: '', kind: 'normal' });

  return (
    <div>
      <SectionHeader
        title="TDS Nature of Payment"
        actions={<Button variant="primary" size="sm" onClick={() => open()}><Plus size={14} /> New section</Button>}
      />

      <Table<TdsSection>
        storageKey="acc-books-tds-sections"
        title="TDS Nature of Payment"
        count={`${rows.length} section${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.code}
        loading={query.isFetching && rows.length === 0}
        empty="No TDS sections."
        columns={[
          { key: 'code', header: 'Section', width: '90px', cell: (r) => <strong className="font-mono">{r.code}</strong> },
          { key: 'nature', header: 'Nature of payment', minWidth: '180px', cell: (r) => <>{r.nature}{r.oldSection && <span className="text-[var(--text-muted)]"> · old {r.oldSection}</span>}</> },
          { key: 'rates', header: 'Rate (Company / Others)', width: '150px', cell: (r) => `${r.rateCompany}% / ${r.rateOther}%` },
          { key: 'limits', header: 'Deduct when', minWidth: '170px', cell: limits },
          { key: 'ledger', header: 'Payable ledger', minWidth: '150px', cell: (r) => r.ledger },
          {
            key: 'actions', header: '', width: '110px', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => open(r)}>Alter</Button>
                {!r.isSystem && (
                  <button type="button" onClick={async () => { if (await confirmDlg(`Delete TDS section ${r.code}?`)) delMut.mutate(r.code); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete section"><Trash2 size={14} /></button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.code ? `Alter TDS Section ${draft.code}` : 'New TDS Section'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={label}>Section</label>
                <Input value={draft.newCode} disabled={!!draft.code} maxLength={10} placeholder="e.g. 1027" onChange={(e) => setDraft({ ...draft, newCode: e.target.value })} />
              </div>
              <div>
                <label className={label}>Old section</label>
                <Input value={draft.oldSection} maxLength={20} placeholder="e.g. 194C" onChange={(e) => setDraft({ ...draft, oldSection: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={label}>Nature of payment</label>
              <Input value={draft.nature} maxLength={120} onChange={(e) => setDraft({ ...draft, nature: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={label}>Rate — company (%)</label>
                <Input type="number" min={0} max={50} step="0.01" value={draft.rateCompany} onChange={(e) => setDraft({ ...draft, rateCompany: e.target.value })} />
              </div>
              <div>
                <label className={label}>Rate — others (%)</label>
                <Input type="number" min={0} max={50} step="0.01" value={draft.rateOther} onChange={(e) => setDraft({ ...draft, rateOther: e.target.value })} />
              </div>
            </div>
            {draft.kind === 'normal' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={label}>Single bill above (₹)</label>
                  <Input type="number" min={0} value={draft.singleLimit} placeholder="—" onChange={(e) => setDraft({ ...draft, singleLimit: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Yearly total above (₹)</label>
                  <Input type="number" min={0} value={draft.yearLimit} placeholder="—" onChange={(e) => setDraft({ ...draft, yearLimit: e.target.value })} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">This section has its own rule ({draft.kind === 'rent' ? 'monthly rent limit' : 'tax on the excess over the yearly limit'}); its limits stay fixed.</p>
            )}
            {!draft.code && <p className="text-xs text-[var(--text-secondary)]">A <b>TDS Payable</b> ledger for this section is created under Duties &amp; Taxes.</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
